import { memo } from "react";

import { useTranslation } from "@workspace/i18n";
import { cn } from "@workspace/ui";
import { buttonVariants } from "@workspace/ui-web/button";
import { Card } from "@workspace/ui-web/card";
import { Icons } from "@workspace/ui-web/icons";
import { Skeleton } from "@workspace/ui-web/skeleton";

import { pathsConfig } from "~/config/paths";
import { TurboLink } from "~/modules/common/turbo-link";

import { Plan } from "./plan/plan";

import type { User } from "@workspace/auth";
import type { BillingConfigPlan } from "@workspace/billing";

const freeFeatures = [
  "pricing.free.features.exports",
  "pricing.free.features.workflow",
  "pricing.free.features.refinement",
  "pricing.free.features.editableReports",
  "pricing.free.features.compatibility",
  "pricing.free.features.emailSupport",
] as const;

const FreePlan = () => {
  const { t } = useTranslation("billing");

  return (
    <div className="min-w-0">
      <Card className="flex h-full flex-col gap-6 px-6 pt-12 pb-6 sm:px-7 md:px-8 md:pt-12 md:pb-8">
        <div>
          <span className="text-lg font-semibold">
            {t("pricing.free.name")}
          </span>
          <p className="py-2 text-4xl font-bold tracking-tighter md:text-5xl">
            {t("pricing.free.price")}
          </p>
          <p className="text-primary text-sm font-semibold sm:text-base">
            {t("pricing.free.description")}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          {freeFeatures.map((feature) => (
            <div key={feature} className="flex items-center gap-3 py-1">
              <div className="bg-primary flex size-5 shrink-0 items-center justify-center rounded-full">
                <Icons.CheckIcon className="text-primary-foreground w-3" />
              </div>
              <span className="text-md">{t(feature)}</span>
            </div>
          ))}
        </div>

        <div className="mt-auto flex flex-col gap-3">
          <TurboLink
            href={pathsConfig.auth.login}
            className={cn(buttonVariants(), "w-full")}
          >
            {t("pricing.free.cta")}
          </TurboLink>
          <p className="text-muted-foreground text-center text-xs leading-5 sm:text-sm">
            {t("pricing.free.noteNoPayment")}
          </p>
        </div>
      </Card>
    </div>
  );
};

interface PlansProps {
  readonly plans: BillingConfigPlan[];
  readonly user: User | null;
}

export const Plans = memo<PlansProps>(({ plans, user }) => {
  return (
    <div className="mt-6 grid w-full grid-cols-1 items-stretch gap-6 sm:grid-cols-2 xl:grid-cols-4">
      <FreePlan />
      {plans.map((plan) => (
        <Plan key={plan.id} plan={plan} user={user} />
      ))}
    </div>
  );
});

export const PlansSkeleton = () => {
  return (
    <div className="mt-6 grid w-full grid-cols-1 items-center gap-6 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="min-w-0">
          <Skeleton className="h-128 w-full" />
        </div>
      ))}
    </div>
  );
};

Plans.displayName = "Plans";
